import { createContext, useCallback, useContext, useState } from 'react';
import { v4 } from 'uuid';

interface ErrorListError {
  error: Error;
  id: string;
  title?: string;
}

interface ErrorListContext {
  errorList: UseErrorListReturn;
}

const _ErrorListContext = createContext<ErrorListContext>({} as any);
export const ErrorListContext = _ErrorListContext.Provider;

export function useErrorListContext() {
  return useContext(_ErrorListContext);
}

export interface UseErrorListReturn {
  addError: (error: Error, id?: string, title?: string) => void;
  reset: () => void;
  removeError: (id: string, options?: { ignoreMissing?: boolean }) => void;
  hasErrors: boolean;
  errors: ErrorListError[];
}

/**
 * Helper method to manage displaying multiple errors
 */
export function useErrorList(): UseErrorListReturn {
  const [errors, setErrors] = useState<ErrorListError[]>([]);
  const addError = useCallback(
    (error: Error, id?: string, title?: string) => {
      // Append the error to the errors list
      setErrors(errors => [
        ...errors.filter(_ => _.id !== id),
        {
          error,
          id: id ?? v4(),
          title
        }
      ]);
    },
    [setErrors]
  );

  const removeError = useCallback(
    (id: string, options?: { ignoreMissing?: boolean }) => {
      const error = errors.find(_ => _.id === id);

      // Validate that the error exists
      if (!error) {
        if (options?.ignoreMissing) return;
        throw new Error(`[useErrorList] Error (id=${id}) was not found`);
      }
      // Remove the error from the list
      setErrors(errors => errors.filter(_ => _.id !== id));
    },
    [errors, setErrors]
  );

  const reset = useCallback(() => setErrors([]), [setErrors]);

  const hasErrors = errors.length > 0;

  return {
    errors,
    addError,
    removeError,
    reset,
    hasErrors
  };
}
